import React, { useState } from 'react';
import { CheckCircle, AlertCircle } from 'lucide-react';

import { useUIState } from '@/core/UIStateProvider';
import { useCompanies } from '@/core/api/queries';

import {
  CompaniesTab,
  UsersTab,
  CarrierIntegrationsTab,
  CreateCompanyModal,
  AddUserModal,
  AddCarrierIntegrationModal,
} from './components';
import { Stats } from './components/Stats';
import { Tabs } from './components/Tabs';
import { EditCarrierIntegrationModal } from './components/EditCarrierIntegrationModal';

const SuperAdminDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'companies' | 'users' | 'carrier-integrations'>('companies');
  const [showCreateCompany, setShowCreateCompany] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [showAddCarrierIntegration, setShowAddCarrierIntegration] = useState(false);
  const [showEditCarrierIntegration, setShowEditCarrierIntegration] = useState(false);
  const [carrierIntegrationToEdit, setCarrierIntegrationToEdit] = useState<string | null>(null);

  const { notifications } = useUIState();

  const currentNotification = notifications[notifications.length - 1];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">System Management</h1>
              <p className="text-gray-600">Manage companies, users and global settings</p>
            </div>
            <span className="px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
              Super Admin
            </span>
          </div>
        </div>
      </div>

      {currentNotification && (
        <div className="fixed top-20 right-4 z-50">
          <div
            className={`flex items-center space-x-2 px-4 py-3 rounded-lg shadow-lg border ${
              currentNotification.type === 'success'
                ? 'bg-green-50 border-green-200 text-green-800'
                : 'bg-red-50 border-red-200 text-red-800'
            }`}
          >
            {currentNotification.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
            <span className="font-medium">{currentNotification.message}</span>
          </div>
        </div>
      )}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <Stats />
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <Tabs activeTab={activeTab} setActiveTab={setActiveTab} />

          <div className="p-6">
            {activeTab === 'companies' && <CompaniesTab setShowCreateCompany={setShowCreateCompany} />}
            {activeTab === 'users' && <UsersTab setShowAddUser={setShowAddUser} />}
            {activeTab === 'carrier-integrations' && (
              <CarrierIntegrationsTab
                setShowAddCarrierIntegration={setShowAddCarrierIntegration}
                setShowEditCarrierIntegration={setShowEditCarrierIntegration}
                setCarrierIntegrationToEdit={setCarrierIntegrationToEdit}
              />
            )}
          </div>
        </div>
      </div>

      <CreateCompanyModal showCreateCompany={showCreateCompany} setShowCreateCompany={setShowCreateCompany} />
      <AddUserModal showAddUser={showAddUser} setShowAddUser={setShowAddUser} />
      <AddCarrierIntegrationModal
        showAddCarrierIntegration={showAddCarrierIntegration}
        setShowAddCarrierIntegration={setShowAddCarrierIntegration}
      />
      <EditCarrierIntegrationModal
        carrierIntegrationId={carrierIntegrationToEdit}
        showEditCarrierIntegration={showEditCarrierIntegration}
        setShowEditCarrierIntegration={setShowEditCarrierIntegration}
      />
    </div>
  );
};

export default SuperAdminDashboard;
